import { type PointerEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJson } from '../api';
import { Shell } from '../components/Shell';
import difficultQuestionsJourneyImage from '../assets/journey-difficult-questions.jpg';
import muslimsSeekingClarityJourneyImage from '../assets/journey-muslims-seeking-clarity.webp';
import foundationsOfFaithJourneyImage from '../assets/journey-foundations-of-faith.jpg';
import reasonScienceAndHumanNatureJourneyImage from '../assets/journey-reason-science-and-human-nature.webp';
import returningAfterDoubtJourneyImage from '../assets/journey-returning-after-doubt.webp';
import quranJourneyImage from '../assets/journey-quran.webp';
import worshipAndThePillarsJourneyImage from '../assets/journey-worship-and-the-pillars.jpg';
import exploreWatercolorBackground from '../assets/explore-watercolor-bg.png';
import './JourneysPage.css';
import { useMeProfile } from '../utils/useMeProfile';
import { orderJourneysFromProfile, profileHint } from '../utils/whyPersonalization';

type Journey = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  audience_json: string | null;
  difficulty: string | null;
  summary_extra: string | null;
  step_count: number | string;
};

const NARRATIVE_ORDER = [
  'difficult-questions-about-islam',
  'for-muslims-seeking-clarity',
  'foundations-of-faith',
  'reason-science-and-human-nature',
  'returning-after-doubt',
  'the-quran-journey',
  'worship-and-the-pillars',
  'exploring-islam-for-the-first-time',
  'christian-questions-about-islam',
];

const JOURNEY_BACKGROUND_IMAGES: Record<string, string> = {
  'difficult-questions-about-islam': difficultQuestionsJourneyImage,
  'for-muslims-seeking-clarity': muslimsSeekingClarityJourneyImage,
  'foundations-of-faith': foundationsOfFaithJourneyImage,
  'reason-science-and-human-nature': reasonScienceAndHumanNatureJourneyImage,
  'returning-after-doubt': returningAfterDoubtJourneyImage,
  'the-quran-journey': quranJourneyImage,
  'worship-and-the-pillars': worshipAndThePillarsJourneyImage,
};

const FALLBACK_BACKGROUND = exploreWatercolorBackground;

function sortJourneys(rows: Journey[]): Journey[] {
  const orderIndex = new Map<string, number>(NARRATIVE_ORDER.map((slug, idx) => [slug, idx]));
  return [...rows].sort((a, b) => {
    const ai = orderIndex.get(a.slug);
    const bi = orderIndex.get(b.slug);
    if (ai != null && bi != null) return ai - bi;
    if (ai != null) return -1;
    if (bi != null) return 1;
    return a.title.localeCompare(b.title);
  });
}

function JourneyScene({ journey, index }: { journey: Journey; index: number }) {
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(index === 0);
  const align = index % 2 === 0 ? 'left' : 'right';
  const backgroundImageSrc = JOURNEY_BACKGROUND_IMAGES[journey.slug] ?? FALLBACK_BACKGROUND;

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.42, rootMargin: '-10% 0px -12% 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handlePointerMove = (e: PointerEvent<HTMLAnchorElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    e.currentTarget.style.setProperty('--wi-cursor-x', `${x.toFixed(2)}%`);
    e.currentTarget.style.setProperty('--wi-cursor-y', `${y.toFixed(2)}%`);
  };

  return (
    <section ref={sectionRef} className={`wi-journey-scene wi-journey-scene--${align}${inView ? ' is-in-view' : ''}`} aria-label={journey.title}>
      <Link
        to={`/journeys/${journey.slug}`}
        className="wi-journey-scene__link"
        aria-label={`Open journey: ${journey.title}`}
        onPointerMove={handlePointerMove}
      >
        <img className="wi-journey-scene__bg" src={backgroundImageSrc} alt="" loading={index < 2 ? 'eager' : 'lazy'} />
        <div className="wi-journey-scene__veil" aria-hidden />
        <div className="wi-journey-scene__cursor" aria-hidden />
        <div className="wi-journey-scene__inner">
          <article className="wi-journey-scene__content">
            <h2 className="wi-journey-scene__title">{journey.title}</h2>
            {(journey.description || journey.summary_extra) && (
              <p className="wi-journey-scene__desc">{journey.description ?? journey.summary_extra}</p>
            )}
          </article>
        </div>
      </Link>
    </section>
  );
}

export function JourneysPage() {
  const [rows, setRows] = useState<Journey[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { explicit, loaded: profileLoaded } = useMeProfile();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiJson<{ journeys: Journey[] }>('/api/journeys');
        if (!cancelled) setRows(res.journeys);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const ordered = useMemo(() => (rows?.length ? sortJourneys(rows) : []), [rows]);

  const adaptEnabled = (explicit?.adaptRecommendations ?? true) !== false;
  const personalizedOrdered = useMemo(() => {
    if (!rows?.length) return [];
    if (!adaptEnabled || !explicit) return ordered;
    if (!profileLoaded) return ordered;
    return orderJourneysFromProfile(ordered, NARRATIVE_ORDER, explicit);
  }, [rows?.length, adaptEnabled, explicit, profileLoaded, ordered]);

  const hint = explicit ? profileHint(explicit) : null;

  return (
    <Shell>
      <div className="wi-journeys-story">
        <header className="wi-journeys-story__hero" aria-labelledby="wi-journeys-story-title">
          <div className="wi-journeys-story__hero-inner">
            <p className="wi-journeys-story__eyebrow">Guided paths</p>
            <h1 id="wi-journeys-story-title" className="wi-journeys-story__title">
              Journeys through Islam
            </h1>
            <p className="wi-journeys-story__lede">
              Begin from where you are - each path is structured, patient, and complete{hint ? ` (${hint})` : ''}.
            </p>
            <a href="#wi-journey-scenes" className="wi-journeys-story__scroll-cue" aria-label="Scroll to journeys">
              Start exploring
            </a>
          </div>
        </header>

        {error ? <p className="wi-journeys-story__error">{error}</p> : null}
        {!rows && !error ? <p className="wi-journeys-story__loading">Loading journeys...</p> : null}

        {personalizedOrdered.length ? (
          <main id="wi-journey-scenes" className="wi-journey-scenes" aria-label="Journey scenes">
            {personalizedOrdered.map((journey, index) => (
              <JourneyScene key={journey.id} journey={journey} index={index} />
            ))}
          </main>
        ) : null}
      </div>
    </Shell>
  );
}
